import React, { FC, useState } from 'react'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Table from 'react-bootstrap/Table'

type SharingStatus = 'Sharing' | 'Not Sharing'
type SectionAction = 'opt-in' | 'opt-out'

interface SharingRow {
    id: string
    displayName: string
    details: string
    firstRequest: string
    mostRecentAccess?: string
    status: SharingStatus
}

interface SharingSectionProps {
    title: string
    description: React.ReactNode
    rows: SharingRow[]
    sectionAction?: SectionAction
    onSectionAction: (action: SectionAction) => void
    optInPlanOptions?: string[]
    optInTypeOptions?: string[]
    firstColumnLabel?: string
    showOptInForm?: boolean
    hideTableWhenEmpty?: boolean
    onOptInSelect?: (
        planName: string,
        type: 'previous' | 'current',
        memberId: string
    ) => void
    onSuggestHealthPlan?: () => void
}

const SharingSection: FC<SharingSectionProps> = ({
    title,
    description,
    rows,
    sectionAction,
    onSectionAction,
    optInPlanOptions = [],
    optInTypeOptions = [],
    firstColumnLabel = 'Health Plan',
    showOptInForm = false,
    hideTableWhenEmpty = false,
    onOptInSelect,
    onSuggestHealthPlan,
}) => {
    const [selectedPlan, setSelectedPlan] = useState('')
    const [selectedType, setSelectedType] = useState('')
    const [memberId, setMemberId] = useState('')

    const isPrevious = selectedType === 'Previous Health Plan'
    const isCurrent = selectedType === 'Current Health Plan'
    const showMemberIdInput = isPrevious || isCurrent

    const memberIdLabel = isPrevious
        ? 'Previous Member ID'
        : isCurrent
            ? 'Current Member ID'
            : ''

    const canSubmitOptIn =
        selectedPlan !== '' &&
        selectedType !== '' &&
        selectedPlan !== 'Select Health Plan' &&
        selectedType !== 'Select Type' &&
        showMemberIdInput &&
        memberId.trim() !== ''

    const handleOptInClick = () => {
        if (!canSubmitOptIn || !onOptInSelect) return

        const normalizedType =
            selectedType === 'Previous Health Plan'
                ? 'previous'
                : 'current'

        onOptInSelect(selectedPlan, normalizedType, memberId.trim())
    }

    return (
        <Row>
            <Col sm={12}>
                <hr className='mt-2 mb-3 border-dark opacity-100' />
                <div className='d-flex align-items-center justify-content-between gap-3'>
                    <h2 className='h3 fw-semibold m-0'>
                        {title}
                    </h2>

                    {sectionAction === 'opt-out' ? (
                        <Button
                            variant='primary'
                            onClick={() =>
                                onSectionAction('opt-out')
                            }
                        >
                            Opt Out
                        </Button>
                    ) : sectionAction === 'opt-in' ? (
                        <Button
                            variant='primary'
                            onClick={() =>
                                onSectionAction('opt-in')
                            }
                        >
                            Opt In
                        </Button>
                    ) : null}
                </div>

                <p className='mt-2 mb-0'>{description}</p>

                {showOptInForm && (
                    <div className='d-flex overflow-x-auto my-2'>
                        <div className='d-flex flex-wrap align-items-center gap-2 w-100'>
                            <span className='h4 fw-bold mb-0'>Opt In</span>

                            <Form.Select
                                size='sm'
                                className='w-auto flex-grow-0'
                                value={selectedPlan}
                                onChange={(event) =>
                                    setSelectedPlan(event.target.value)
                                }
                            >
                                {optInPlanOptions.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </Form.Select>

                            <Form.Select
                                size='sm'
                                className='w-auto flex-grow-0'
                                value={selectedType}
                                onChange={(event) =>
                                    setSelectedType(event.target.value)
                                }
                            >
                                {optInTypeOptions.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </Form.Select>

                            {showMemberIdInput && (
                                <Form.Control
                                    size='sm'
                                    className='w-auto flex-grow-1'
                                    style={{ flexBasis: 0 }}
                                    type='text'
                                    placeholder={memberIdLabel}
                                    aria-label={memberIdLabel}
                                    value={memberId}
                                    onChange={(event) =>
                                        setMemberId(event.target.value)
                                    }
                                />
                            )}

                            <Button
                                className='ms-auto'
                                variant='primary'
                                onClick={handleOptInClick}
                                disabled={!canSubmitOptIn}
                            >
                                Opt In
                            </Button>
                        </div>
                    </div>
                )}
                {showOptInForm && onSuggestHealthPlan && (
                    <p className='mt-0 mb-2 small'>
                        Don&apos;t see your plan on this list?{' '}
                        <button
                            type='button'
                            className='btn btn-link p-0 align-baseline'
                            onClick={onSuggestHealthPlan}
                        >
                            Let us know
                        </button>
                    </p>
                )}

                {(!hideTableWhenEmpty || rows.length > 0) && (
                    <Table
                        responsive
                        borderless
                        size='sm'
                        className='align-middle mb-2'
                    >
                        <thead className='table-light'>
                            <tr className='border-bottom'>
                                <th className='fw-bold'>
                                    {firstColumnLabel}
                                </th>
                                <th className='fw-bold'>First Request</th>
                                <th className='fw-bold'>Most Recent Access</th>
                                <th className='fw-bold'>Status</th>
                            </tr>
                        </thead>

                        <tbody>
                            {rows.map((row) => (
                                <tr key={row.id}>
                                    <td>
                                        <div className='fw-bold'>
                                            {row.displayName}
                                        </div>

                                        <div className='text-muted small'>
                                            {row.details}
                                        </div>
                                    </td>

                                    <td>{row.firstRequest}</td>

                                    <td>
                                        {row.mostRecentAccess ?? '—'}
                                    </td>

                                    <td>
                                        {row.status === 'Sharing'
                                            ? 'Sharing On'
                                            : 'Sharing Off'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Col>
        </Row>
    )
}

export default SharingSection